import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, CircularProgress, Divider, IconButton, Stack, Typography} from '@mui/material';
import {Outlet, useNavigate, useParams} from 'react-router-dom';

import {ArrowBack, CheckCircleGreen, CheckCircleGrey, Cross, Delete, Download} from '@icons/index';
import {useTranslation} from 'react-i18next';
import {Colors} from '@constants/colors.constants';
import SearchComponent from '@components/inputs/search/Search.component';
import {InvoiceDetails, invoicesService, InvoiceStatus} from '@store/invoices/invoices';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import {map, switchMap, tap} from 'rxjs';
import {NumberExt} from '@utils/extensions/number.extension';
import {ExecutionLine} from '@store/invoices/executionLines';
import {useSnackbar} from 'notistack';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {PaymentTerm, TaxRule} from '@store/common/organisation.enums';
import dayjs from 'dayjs';
import {Block, CancelOutlined, Edit, Launch, PictureAsPdfOutlined, Replay} from '@mui/icons-material';
import SelectComponent, {SelectItem} from '@components/inputs/select/Select.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridValueFormatterParams, GridValueGetterParams, GridValueSetterParams,} from '@mui/x-data-grid-pro';
import NewTableComponent from '@components/table/NewTable.component';
import {OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {ServiceKind, serviceKindsService} from '@store/settings/serviceKinds';
import DeleteBillModal from '@screens/admin/invoices/bills/details/DeleteBill.modal';
import {History, historyService, HistoryType} from '@store/settings/history';
import {AdminRoutes, SettingsRoutes} from '@utils/routes.utils';
import CancelBillModal from '@screens/admin/invoices/bills/cancel/CancelBill.modal';
import {wasteFamiliesService} from '@store/settings/wasteFamilies';
import UpdateBillAddressModal from '@screens/admin/invoices/bills/details/UpdateBillAddress.modal';
import ExpandableIconButtonComponent from '@components/buttons/ExpandableIconButton.component';

const BillDetailsScreen = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const {invoiceId, jobInvoiceId} = useParams();

  const handleClose = () => navigate('..', {replace: true});

  const [invoiceLineToAdd, setInvoiceLineToAdd] = useState<Partial<ExecutionLine> | false>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [billDetails, setBillDetails] = useState<InvoiceDetails | undefined>();
  const [history, setHistory] = useState<History[] | undefined>();
  const [selectedInvoiceLines, setSelectedInvoiceLines] = useState<number[]>([]);
  const [showDeleteBill, setShowDeleteBill] = useState<boolean>(false);
  const [showCancelBill, setShowCancelBill] = useState<boolean>(false);
  const [showUpdateAddress, setShowUpdateAddress] = useState<boolean>(false);

  const searchedExecutionLines = useMemo(() => billDetails?.clientInvoiceLines ?? [], [billDetails?.clientInvoiceLines]);

  useEffect(() => {
    if (invoiceId) {
      setLoading(true);
      invoicesService.getInvoiceDetails(invoiceId).subscribe({
        next: setBillDetails,
        complete: () => setLoading(false),
      });
      historyService.getHistoryByResourceId(invoiceId, 'INVOICE').subscribe({
        next: (res) => setHistory(res.sort((h1, h2) => new Date(h2.createdAt).getTime() - new Date(h1.createdAt).getTime())),
        complete: () => setLoading(false),
      });
    }
  }, [invoiceId]);

  const handleEditMode = (
    executionLine: ExecutionLine & { 'execution.executedAt': Date; 'serviceKind.kind': SelectItem; waste: SelectItem }
  ) => {
    if (!invoiceId) return {} as ExecutionLine;

    if (executionLine.isNew) {
      const updatedOrphanLine = {
        name: executionLine.name,
        kind: executionLine['serviceKind.kind'].data?.id,
        executedAt: executionLine['execution.executedAt'],
        puBuy: executionLine.puSell,
        puSell: executionLine.puSell,
        quantity: executionLine.quantity,
        unit: executionLine['serviceKind.kind'].data?.execUnitName1,
        serviceKind: executionLine['serviceKind.kind'].data as ServiceKind,
        waste: executionLine.waste?.value,
      };

      invoicesService.addInvoiceInvoiceLine(invoiceId, updatedOrphanLine).subscribe({
        next: (bd) => {
          setBillDetails(bd);
          setInvoiceLineToAdd(false);
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });

      return {...executionLine, ...updatedOrphanLine, kind: executionLine['serviceKind.kind']};
    } else {
      const updatedExecutionLine = {
        puSell: executionLine.puSell,
        quantity: executionLine.quantity,
        name: executionLine.name,
      };

      invoicesService
        .patchInvoiceLine(invoiceId, executionLine.id, updatedExecutionLine)
        .pipe(switchMap(() => invoicesService.getInvoiceDetails(invoiceId).pipe(tap(setBillDetails))))
        .subscribe({
          error: (err) => enqueueSnackbar(err.text, err.options),
        });

      return {...executionLine, ...updatedExecutionLine};
    }
  };

  const handleRemoveExecutionLine = (revertLines: boolean) => {
    if (!invoiceId) return;
    invoicesService
      .removeInvoiceInvoiceLines(invoiceId, selectedInvoiceLines, revertLines)
      .pipe(switchMap(() => invoicesService.getInvoiceDetails(invoiceId).pipe(tap(setBillDetails))))
      .subscribe({
        next: () => enqueueSnackbar(t('delete.success.billExecLine'), {variant: 'success'}),
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  const handleDeleteInvoice = () => {
    if (invoiceId) {
      invoicesService.deleteInvoice(invoiceId).subscribe({
        next: () => navigate('..'),
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  const handleDownloadPdf = () => {
    if (invoiceId) {
      invoicesService.printInvoice(invoiceId).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  const handleDownloadCsv = () => {
    if (invoiceId) {
      invoicesService.invoiceLinesCsv(invoiceId).subscribe({
        error: (err) => {
          console.error(err);
          enqueueSnackbar(err.text, err.options);
        },
      });
    }
  };

  const handleUpdateBill = (status?: InvoiceStatus, comment?: string) => {
    if (!invoiceId) return;
    invoicesService.updateInvoice(invoiceId, status, comment).subscribe({
      next: setBillDetails,
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const handleResetAccountingTransferDate = () => {
    if (!invoiceId) return;
    invoicesService.resetAccountingTransferDate(invoiceId).subscribe({
      next: setBillDetails,
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };


  if (loading) {
    return (
      <Stack height="100%" justifyContent="center" alignItems="center">
        <CircularProgress size={30}/>
      </Stack>
    );
  }

  const actionsAvailable = billDetails?.status === InvoiceStatus.DRAFT;

  return (
    <>
      <Stack pt="34px" px="24px" pb="20px" className="scrollable">
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" ml="-9px" mt="-20px">
            <IconButton onClick={handleClose}>
              <ArrowBack width={12} height={12}/>
            </IconButton>
            <Typography fontSize="18px" fontWeight="700">
              {t('invoices.bills.details.title')}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" pt="20px" justifyContent="space-between">
          <Stack spacing={1} width="fit-content">
            <Stack direction="row" alignItems="center" pr="127px" spacing={1}>
              <Typography fontSize="20px" fontWeight="700">
                {t(billDetails?.type === 'DEFAULT' ? 'invoices.bills.details.numberStatement' : 'invoices.bills.details.cancelNumberStatement', {number: billDetails?.invoiceRef ?? ''})}
              </Typography>
              <Typography fontSize="18px" fontWeight="700" color={Colors.textWithOpacity}>
                {`(${billDetails?.clientName} o ${billDetails?.facturationType})`}
              </Typography>
            </Stack>
            <Divider
              orientation="horizontal"
              variant="fullWidth"
              style={{borderWidth: '2px', borderColor: Colors.primary, maxWidth: '100%'}}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <ExpandableIconButtonComponent variant="outlined" color="primary" onClick={() => setShowUpdateAddress(true)}
                                           startIcon={<Edit/>}
                                           title={t('global.updateAddress')}/>
            <ExpandableIconButtonComponent
              variant="outlined"
              color="primary"
              onClick={handleDownloadPdf}
              startIcon={<PictureAsPdfOutlined style={{color: '#172134', transform: 'scale(1.1)'}}/>}
              title={t('invoices.invoicesValidation.details.download')}
            />

            <ExpandableIconButtonComponent variant="outlined" color="primary" onClick={handleDownloadCsv}
                                           startIcon={<Download/>} title={t('global.exportCSV')}/>
            {!jobInvoiceId && actionsAvailable && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowDeleteBill(true)}
                startIcon={<Delete/>}>
                {t('global.delete')}
              </Button>
            )}
            {
              !actionsAvailable && billDetails?.type === 'DEFAULT' && (
                <ExpandableIconButtonComponent
                  variant="outlined"
                  color="primary"
                  onClick={() => setShowCancelBill(true)}
                  startIcon={<Block/>}
                  title={t('invoices.bills.details.cancel')}
                />
              )
            }
            {
              billDetails?.type === 'CANCEL' && billDetails.invoiceOriginId && (
                <ExpandableIconButtonComponent
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate(`../${billDetails?.invoiceOriginId}`)}
                  title={t('invoices.bills.details.seeOriginalBill')}
                  startIcon={<Launch/>}
                />

              )
            }
          </Stack>
        </Stack>
      </Stack>
      <Stack overflow="auto" className="scrollable" pb="25px" px="24px" flex={1} spacing={3}>
        <Stack mt="12px" p="18px" bgcolor={Colors.white} direction="row">
          <Stack spacing={3} flex={1}>
            <Stack direction="row" spacing={2}>
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.documentDate')}*
                </Typography>
                <DatePickerComponent color="secondary" readOnly value={billDetails?.documentDate}/>
              </Stack>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.accountantTransferDate')}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Box flex={1}>
                    <TextFieldComponent
                      readOnly={true}
                      value={
                        billDetails?.accountingTransferDate
                          ? dayjs(billDetails?.accountingTransferDate).format('DD/MM/YYYY HH:mm')
                          : ''
                      }
                    />
                  </Box>
                  <Button variant="outlined" color="primary" onClick={handleResetAccountingTransferDate}>
                    {t('invoices.purchaseStatement.details.removeTransferDate')}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.reminderDate')}*
                </Typography>
                <DatePickerComponent color="secondary" readOnly value={billDetails?.reminderDate}/>
              </Stack>
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.dueDate')}*
                </Typography>
                <DatePickerComponent color="secondary" readOnly value={billDetails?.dueDate}/>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.totalAmountNoVatInCents')}
                </Typography>
                <NumberTextFieldComponent
                  readOnly
                  endAdornment={
                    <Typography fontSize={14} fontWeight="400">
                      {billDetails?.currency}
                    </Typography>
                  }
                  value={NumberExt.round((billDetails?.totalAmountNoVatInCents ?? 0) / 100, 2)}
                />
              </Stack>
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.totalAmountVatIncludedInCents')}
                </Typography>
                <NumberTextFieldComponent
                  readOnly
                  endAdornment={
                    <Typography fontSize={14} fontWeight="400">
                      {billDetails?.currency}
                    </Typography>
                  }
                  value={NumberExt.round((billDetails?.totalAmountVatIncludedInCents ?? 0) / 100, 2)}
                />
              </Stack>
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.changeRate')}
                </Typography>
                <NumberTextFieldComponent
                  readOnly
                  value={billDetails?.changeRate}
                />
              </Stack>
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.totalAmountVatIncludedCHF')}
                </Typography>
                <NumberTextFieldComponent
                  readOnly
                  endAdornment={
                    <Typography fontSize={14} fontWeight="400">
                      CHF
                    </Typography>
                  }
                  value={NumberExt.round((billDetails?.totalAmountVatIncludedInCentsCHF ?? 0) / 100, 2)}
                />
              </Stack>
            </Stack>
            <Stack spacing={0.5}>
              <Typography fontSize="16px" fontWeight="400">
                {t('invoices.bills.details.comment')}
              </Typography>
              <TextFieldComponent
                handleKeyPress={(evt) => {
                  if (((evt.target as any).value?.length ?? 0) >= 150) evt.preventDefault();
                }}
                value={billDetails?.comment}
                handleChange={(value) => {
                  if (billDetails) {
                    setBillDetails({...billDetails, comment: value});
                  }
                }}
                handleBlur={(comment) => handleUpdateBill(undefined, comment)}
              />
            </Stack>
          </Stack>
          <Stack spacing={3} pl="195px" flex={1}>
            <Stack direction="row" spacing={2}>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.organisation')}*
                </Typography>
                <TextFieldComponent readOnly value={billDetails?.clientName ?? ''}/>
              </Stack>
              <Stack spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.status')}*
                </Typography>
                <SelectComponent
                  inputWidth={150}
                  readOnly={billDetails?.status === InvoiceStatus.DRAFT}
                  items={
                    billDetails?.status === InvoiceStatus.DRAFT
                      ? InvoiceStatus.selectItems().slice(0, 1)
                      : InvoiceStatus.selectItems().slice(1)
                  }
                  handleChange={(status) => handleUpdateBill(status)}
                  value={billDetails?.status ?? ''}
                />
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.taxRule')}*
                </Typography>
                <TextFieldComponent
                  readOnly
                  value={TaxRule.selectItems().find((t) => t.value === billDetails?.taxRule)?.label ?? ''}
                />
              </Stack>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.paymentTermNumDays')}*
                </Typography>
                <TextFieldComponent
                  readOnly
                  value={
                    PaymentTerm.selectItems().find((t) => t.value === billDetails?.paymentTermNumDays)?.label ?? ''
                  }
                />
              </Stack>
            </Stack>
            <Stack spacing={0.5}>
              <Typography fontSize="16px" fontWeight="400">
                {t('invoices.bills.details.address')}
              </Typography>
              <TextFieldComponent
                multiline
                rows={5}
                readOnly
                value={[billDetails?.wireToAccountName, billDetails?.wireToAccountAddress, `${billDetails?.wireToAccountZip || ''} ${billDetails?.wireToAccountCity || ''}`].join('\n')}/>
            </Stack>
            {billDetails?.type === 'CANCEL' && <Stack spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.details.cancellationReason')}
                </Typography>
                <TextFieldComponent
                    readOnly
                    value={billDetails.cancellationReason}/>
            </Stack>}
          </Stack>
        </Stack>
        <Stack mt="12px" spacing={2}>
          {actionsAvailable && <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                  variant="outlined"
                  color="primary"
                  disabled={!selectedInvoiceLines.length}
                  onClick={() => handleRemoveExecutionLine(true)}
                  startIcon={<Replay style={{height: 22, width: 22}}/>}
              >
                {t('invoices.bills.details.putInBacklog')}
              </Button>
          </Stack>}
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Box width="500px">
              <SearchComponent/>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" color="primary" startIcon={<Cross/>} size="small">
                {t('global.resetFilters')}
              </Button>
              <Divider variant="fullWidth" orientation="vertical" sx={{height: '30px'}}/>
              <Button variant="outlined" color="primary" size="small">
                {t('global.filters')}
              </Button>
            </Stack>
          </Stack>
          <Stack minHeight={500} pb="20px">
            <NewTableComponent
              columns={[
                COLUMN_TYPES.ICON({
                  field: 'execution.isCancelled',
                  color: 'secondary',
                  label: '',
                  minWidth: 30,
                  width: 30,
                  renderIcon: (executionLine: ExecutionLine) => executionLine.cancelledByLineId ?
                    <CancelOutlined fontSize="small"/> : <></>,
                }),
                COLUMN_TYPES.DATE({
                  field: 'execution.executedAt',
                  cellClassName: () => 'secondary',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.executedAt'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.executedAt,
                  valueFormatter: (params: GridValueFormatterParams) =>
                    params.value ? t('global.date', {date: new Date(params.value)}) : '',
                  width: 200,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'execution.clientOrganisationUnitName',
                  label: t('invoices.executions.clientOrganisationUnitName'),
                  valueGetter: (params: GridValueGetterParams<ExecutionLine, ExecutionLine>) => params.row.clientOrganisationUnitName,
                  width: 250,
                }),
                actionsAvailable ? COLUMN_TYPES.INPUT({
                  field: 'name',
                  color: 'secondary',
                  label: t('invoices.executions.table.name'),
                  fullWidth: true,
                  minWidth: 250,
                }) : COLUMN_TYPES.TEXT({
                  field: 'name',
                  color: 'secondary',
                  label: t('invoices.executions.table.name'),
                  fullWidth: true,
                  minWidth: 250,
                }),
                actionsAvailable ? COLUMN_TYPES.ASYNC_SELECT({
                  field: 'serviceKind.kind',
                  cellClassName: () => 'bold',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.type'),
                  getOptions: (search) =>
                    serviceKindsService
                      .searchServiceKinds(search)
                      .pipe(
                        map((options) => options.data.map((o) => ({label: o.kind || '-', value: o.id, data: o})))
                      ),

                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.kind,
                  width: 250,
                }) : COLUMN_TYPES.TEXT({
                  field: 'serviceKind.kind',
                  cellClassName: () => 'bold',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.type'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.kind,
                  width: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'additionalInformation',
                  color: 'secondary',
                  label: t('invoices.executions.table.additionalInformation'),
                  minWidth: 100,
                }),
                actionsAvailable ? COLUMN_TYPES.ASYNC_SELECT({
                  field: 'waste',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.waste'),
                  getOptions: (search) =>
                    wasteFamiliesService
                      .searchWaste(search)
                      .pipe(
                        map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id, data: o})))
                      ),

                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.waste,
                  width: 250,
                }) : COLUMN_TYPES.TEXT({
                  field: 'waste',
                  color: 'secondary',
                  label: t('invoices.executions.table.waste'),
                  // valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.waste,
                  width: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'container',
                  color: 'secondary',
                  label: t('invoices.executions.table.container'),
                  // valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.container,
                  width: 200,
                }),
                actionsAvailable ? COLUMN_TYPES.NUMBER({
                  field: 'executionLine.quantity',
                  label: t('invoices.executions.table.quantity'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.quantity,
                  valueSetter: (params: GridValueSetterParams) => ({
                    ...params.row,
                    quantity: params.value ?? 0,
                    totalSell: (params.value ?? 0) * (params.row.puSell ?? 0) * 100,
                  }),
                  width: 100,
                }) : COLUMN_TYPES.TEXT({
                  field: 'executionLine.quantity',
                  label: t('invoices.executions.table.quantity'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.quantity,
                  width: 100,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'serviceKind.execUnitName1',
                  color: 'secondary',
                  label: t('invoices.executions.table.unit'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.unit,
                  width: 100,
                }),
                actionsAvailable ? COLUMN_TYPES.NUMBER({
                  field: 'executionLine.puSell',
                  label: t('invoices.executions.table.puSell'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                    params.row.puSell ? NumberExt.round(params.row.puSell / 100, 2) : undefined,
                  valueSetter: (params: GridValueSetterParams) => ({
                    ...params.row,
                    puSell: (params.value ?? 0) * 100,
                    totalSell: (params.value ?? 0) * (params.row.quantity ?? 0) * 100,
                  }),
                  width: 100,
                }) : COLUMN_TYPES.TEXT({
                  field: 'executionLine.puSell',
                  label: t('invoices.executions.table.puSell'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                    params.row.puSell ? NumberExt.round(params.row.puSell / 100, 2) : undefined,
                  width: 100
                }),
                COLUMN_TYPES.TEXT({
                  field: 'totalSell',
                  label: t('invoices.executions.table.totalSell'),
                  valueFormatter: (params: GridValueFormatterParams) =>
                    params.value ? NumberExt.round(params.value / 100, 2) : '',
                  width: 100,
                }),
                COLUMN_TYPES.ICON({
                  field: 'supplierInvoiceLineId',
                  label: t('invoices.executions.table.purchaseStatementShort'),
                  renderIcon: (executionLine: ExecutionLine) => {
                    if (!executionLine.supplierInvoicingRequired) return <Box>-</Box>;
                    return executionLine.supplierInvoiceLineId ? <CheckCircleGreen/> : <CheckCircleGrey/>;
                  },
                  width: 40,
                }),
                COLUMN_TYPES.ACTIONS({
                  handleShowRow: (executionLine: ExecutionLine) =>
                    executionLine.executionId ? navigate(`${executionLine.executionId}`) : undefined,
                  disabledShowRow: (executionLine: ExecutionLine) => !executionLine.executionId,
                  handleCancel: () => setInvoiceLineToAdd(false),
                  otherActions: actionsAvailable ? [{type: OtherActionType.EDIT}] : [],
                }),
              ]}
              paginationMode="client"
              disablePagination
              disableRowSelection={!actionsAvailable}
              disableEdition={!actionsAvailable}
              sortingMode="client"
              loading={loading}
              autoHeight={false}
              sortModel={[
                {field: 'execution.executedAt', sort: 'asc'},
                {field: 'waste', sort: 'asc'},
                {
                  field: 'container',
                  sort: 'asc',
                },
              ]}
              handleRowSelected={setSelectedInvoiceLines}
              rowsSelected={selectedInvoiceLines}
              handleRowChanged={handleEditMode}
              data={((invoiceLineToAdd ? [invoiceLineToAdd as ExecutionLine] : [])).concat(searchedExecutionLines)}/>
          </Stack>
        </Stack>
        <Stack spacing={2} minHeight={300} pb="20px">
          <Typography fontWeight="700" fontSize="18px">
            {t('invoices.purchaseStatement.details.sites.title')}
          </Typography>
          <NewTableComponent
            columns={[
              COLUMN_TYPES.TEXT_BOLD({
                field: 'clientName',
                label: t('invoices.purchaseStatement.details.sites.table.client'),
                valueFormatter: (params) => (params.value ? params.value.toUpperCase() : ''),
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'clientUnitName',
                label: t('invoices.purchaseStatement.details.sites.table.site'),
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'phone',
                label: t('invoices.purchaseStatement.details.sites.table.phone'),
                width: 150,
              }),
              COLUMN_TYPES.TEXT({
                field: 'address',
                cellClassName: () => 'pre-line',
                color: 'secondary',
                label: t('invoices.purchaseStatement.details.sites.table.address'),
                valueFormatter: (params: GridValueFormatterParams) =>
                  `${params.value.street}\n${params.value.zip} ${params.value.city}`,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'country',
                label: t('invoices.purchaseStatement.details.sites.table.country'),
                valueGetter: (params: GridValueGetterParams) => params.row.address.country,
                valueFormatter: (params) => (params.value ? params.value.toUpperCase() : ''),
                width: 150,
              }),
            ]}
            paginationMode="client"
            disablePagination
            disableRowSelection
            data={billDetails?.clients ?? []}
          />
        </Stack>
        <Stack spacing={2} minHeight={300}>
          <Typography fontWeight="700" fontSize="18px">
            {t('routes.history')}
          </Typography>
          <NewTableComponent
            loading={loading}
            columns={[
              COLUMN_TYPES.TEXT_BOLD({
                field: 'id',
                label: t('settings.histories.table.id'),
                valueGetter: (params: GridValueGetterParams<any, History>) => `${params.row.id}`,
                fullWidth: true,
                width: 50,
              }),
              COLUMN_TYPES.TEXT({
                field: 'createdAt',
                label: t('settings.histories.table.createdAt'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, History>) => params.row.createdAt,
                valueFormatter: (params) => (params.value ? t('global.date', {date: new Date(params.value)}) : '-'),
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'user',
                label: t('settings.histories.table.user'),
                fullWidth: true,
                valueGetter: (params: GridValueGetterParams<any, History>) => {
                  if (!params.row.authorLastName || !params.row.authorFirstName) return ' - ';

                  return `${params.row.authorLastName} ${params.row.authorFirstName}`;
                },
                minWidth: 150,
              }),
              COLUMN_TYPES.TEXT({
                field: 'tableName',
                label: t('settings.histories.table.tableName'),
                fullWidth: true,
                minWidth: 150,
              }),
              COLUMN_TYPES.TEXT({
                field: 'eventType',
                label: t('settings.histories.table.type'),
                valueFormatter: (params) =>
                  HistoryType.selectItems().find((s) => s.value === params.value)?.label || '',
                fullWidth: true,
                minWidth: 120,
              }),
              COLUMN_TYPES.ACTIONS({
                handleShowRow: (history: History) => navigate(`/${AdminRoutes.SETTINGS}/${SettingsRoutes.HISTORY}/${history.id}`),
              }),
            ]}
            paginationMode="client"
            disablePagination
            disableRowSelection
            data={history}
          />
        </Stack>
      </Stack>
      <Outlet/>
      {showDeleteBill && (
        <DeleteBillModal
          handleClose={() => setShowDeleteBill(false)}
          handleDelete={() => {
            handleDeleteInvoice();
          }}
        />
      )}
      {
        showCancelBill && (
          <CancelBillModal billDetails={billDetails} handleClose={() => setShowCancelBill(false)}/>
        )
      }
      {
        showUpdateAddress && (
          <UpdateBillAddressModal invoiceDetails={billDetails} handleClose={(invoiceDetails) => {
            if (invoiceDetails) {
              setBillDetails(invoiceDetails);
            }
            setShowUpdateAddress(false);
          }}/>
        )
      }
    </>
  );
};

export default BillDetailsScreen;
